import { useState, useEffect } from 'react';
import { Link } from 'react-scroll';
import { FiGithub, FiLinkedin, FiMail, FiArrowDown, FiDownload, FiEye, FiMapPin, FiSend, FiExternalLink } from 'react-icons/fi';
import picture from './picture.png';
import c1 from './c1.png';
import c2 from './c2.png';
import c3 from './c3.png';
import c4 from './c4.png';
import c5 from './c5.png';
import './App.css';

// ============================================================
// DATA — Edit everything here
// ============================================================
const personalInfo = {
  name: "Dwayne jizo B. Lagamayo",
  firstName: "Dwayne Jizo",
  title: "Aspiring Data & Product Engineer",
  subtitle: "Computer Engineering Student | AI/ML Enthusiast | Data Architect",
  tagline: "Engineering intelligent data pipelines and scalable AI products from the ground up.",
  school: "FAITH Colleges",
  course: "Bachelor of Science in Computer Engineering",
  yearLevel: "3rd Year",
  email: "s2023101530@firstasia.edu.ph",
  github: "https://github.com/dwaynelagamayo",
  linkedin: "https://linkedin.com/in/dwaynelagamayo",
  location: "Philippines",
  photo: picture,
};

const aboutMe = {
  intro: "I am Dwayne Jizo B. Lagamayo, a 3rd-year Computer Engineering student at FAITH Colleges with a profound passion for data architecture, intelligent systems, and end-to-end product engineering.",
  whyCPE: "I chose Computer Engineering because I wanted a deep, foundational understanding of how systems process information — from hardware circuits up to complex machine learning algorithms. This engineering rigor allows me to design scalable software products connected by intelligent data.",
  interests: "My primary interests lie in Data Engineering, Machine Learning, and AI infrastructure. I enjoy exploring how predictive models can be deployed into real, user-facing software products to solve complex real-world challenges.",
  goals: "My goal is to become a Data / Product Engineer and Machine Learning / AI Engineer. I aspire to build robust backend data pipelines, engineer self-learning AI systems, and drive the development of innovative, scalable tech products.",
  strengths: ["Data Engineering", "Machine Learning", "Product Development", "System Architecture", "Innovation Mindset"],
};

const education = [
  {
    school: "First Asia Institute of Technology and Humanities Colleges",
    degree: "Bachelor of Science in Computer Engineering",
    period: "2023 – Present",
    yearLevel: "3rd Year",
    status: "Current",
    details: "Specializing in embedded systems architecture, FPGA design, real-time systems, and hardware-software co-design with emphasis on IoT and automation technologies.",
    relevantCourses: ["Digital Logic Design", "Microprocessors & Microcontrollers", "FPGA Development with Verilog", "Real-Time Operating Systems", "Embedded C Programming", "IoT Systems Design", "Signal Processing", "Computer Architecture & Organization"],
  },
  {
    school: "La Cosolacion College Tanauan",
    degree: "Senior High School — STEM Strand",
    period: "2021 – 2023",
    yearLevel: "",
    status: "Graduated",
    details: "Completed Senior High School under the STEM strand (STEM Engineering).",
    relevantCourses: ["Pre-Calculus", "Basic Calculus", "General Physics 1 & 2", "Statistics and Probability", "Empowerment Technologies (ICT)", "General Chemistry"],
  },
];

const skills = {
  programmingLanguages: [
    { name: "C++", level: 80 },
    { name: "Python", level: 85 },
    { name: "Java", level: 70 },
    { name: "JavaScript", level: 75 },
    { name: "Verilog HDL", level: 85 },
  ],
  webDevelopment: [
    { name: "HTML / CSS", level: 75 },
    { name: "ReactJS", level: 70 },
  ],
  hardwareTools: [
    { name: "Arduino", level: 80 },
    { name: "ESP32-S3", level: 85 },
    { name: "FPGA / Verilog", level: 85 },
    { name: "Multimeter & Oscilloscope", level: 70 },
  ],
  softSkills: ["Problem Solving", "Teamwork", "Communication", "Time Management", "Critical Thinking"],
};

const projects = [
  { id: 1, title: "Autonomous Sumobot", category: "IoT & Embedded Systems", description: "Designed and programmed an autonomous sumo robot capable of detecting and pushing opponents out of a ring using ultrasonic and infrared sensors.", tools: ["Arduino", "C++", "Ultrasonic Sensors", "Motor Drivers"], role: "Hardware Developer & Programmer", github: "", demo: "", image: null },
  { id: 2, title: "Elevator System Implementation on FPGA", category: "FPGA & Digital Logic", description: "Developed a finite state machine (FSM) based control system for an elevator model, successfully synthesizing and implementing it on an FPGA board.", tools: ["FPGA", "Verilog HDL", "Quartus/Vivado", "Digital Logic"], role: "FPGA Engineer", github: "", demo: "", image: null },
  { id: 3, title: "Tetris Game Implementation on FPGA", category: "FPGA & Digital Logic", description: "Recreated the classic Tetris game at the hardware level using HDL, featuring real-time VGA output, scoring mechanics, and input debouncing.", tools: ["FPGA", "Verilog HDL", "VGA Controller", "I/O Handling"], role: "Hardware Game Designer", github: "", demo: "", image: null },
  { id: 4, title: "ALU Calculator on Verilog HDL", category: "FPGA & Digital Logic", description: "Engineered a complete Arithmetic Logic Unit (ALU) capable of performing standard mathematical and bitwise operations using Verilog HDL.", tools: ["Verilog HDL", "ModelSim", "Digital Logic"], role: "Logic Design Engineer", github: "", demo: "", image: null },
  { id: 5, title: "Resto Website", category: "Web Development", description: "Developed a responsive and dynamic website for a restaurant featuring an interactive menu, reservation system, and a modern UI.", tools: ["HTML", "CSS", "JavaScript", "ReactJS"], role: "Front-End Web Developer", github: "", demo: "", image: null },
  { id: 6, title: "Smart Lock Room System", category: "IoT & Embedded Systems", description: "Created a secure room entry system integrating RFID tags, keypad entry, and an automated unlocking mechanism controlled by a microcontroller.", tools: ["Arduino/ESP32", "RFID (RC522)", "C++", "Servo Motors"], role: "Embedded Systems Developer", github: "", demo: "", image: null },
  { id: 7, title: "IoT Incubator Control System", category: "IoT & Embedded Systems", description: "Built a smart incubator capable of precisely regulating temperature and humidity, while streaming real-time sensor data to an IoT cloud dashboard.", tools: ["NodeMCU/ESP8266", "DHT11 Sensors", "ThingSpeak/Blynk", "C++"], role: "IoT Solutions Architect", github: "", demo: "", image: null },
  { id: 8, title: "Personal Portfolio Website", category: "Web Development", description: "Built a high-performance, responsive portfolio website to showcase engineering projects, academic background, and technical skills using modern web frameworks.", tools: ["ReactJS", "CSS", "JavaScript", "Vercel"], role: "Front-End Developer", github: "", demo: "", image: null },
  { id: 9, title: "Hardware Implementation of BCD to Gray Code Converter", category: "FPGA & Digital Logic", description: "Designed and wired a physical digital logic circuit on a breadboard translating Binary Coded Decimal (BCD) directly into Gray Code using discrete IC logic gates.", tools: ["Logic Gates (74xx)", "Breadboard", "Power Supply"], role: "Circuit Engineer", github: "", demo: "", image: null },
  { id: 10, title: "Student Task Manager Webapp", category: "Web Development", description: "Programmed a productivity web application enabling students to track assignments, schedule tasks, and analyze academic deadlines effectively.", tools: ["ReactJS", "Node.js", "Express", "MongoDB"], role: "Full-Stack Developer", github: "", demo: "", image: null },
  { id: 11, title: "Smart Home Automation System", category: "IoT & Embedded Systems", description: "Integrated various actuators and sensors to automate lighting, fans, and security features within a home network, controlled via a custom mobile or web app.", tools: ["ESP32", "Relays", "MQTT", "Python/C++"], role: "Lead Embedded Developer", github: "", demo: "", image: null },
  { id: 12, title: "Digital Logic Circuit", category: "FPGA & Digital Logic", description: "Conceptualized, minimized, and simulated an array of complex combinational and sequential digital logic circuits demonstrating proficiency in Boolean algebra.", tools: ["Proteus", "Logisim", "Boolean Algebra"], role: "Digital Analyst", github: "", demo: "", image: null },
  { id: 13, title: "Secure User Authentication System", category: "Web Development", description: "Developed a complete authentication pipeline featuring secure user registration, email-based One-Time Password (OTP) verification, and encrypted session management.", tools: ["ReactJS", "Node.js", "Express", "MongoDB", "Nodemailer"], role: "Full-Stack Developer", github: "", demo: "", image: null },
  { id: 14, title: "Image Processing Shape Detector", category: "Computer Vision & AI", description: "Developed a computer vision script utilizing OpenCV to detect, classify, and track geometric shapes in real-time from a live camera feed.", tools: ["Python", "OpenCV", "NumPy"], role: "Computer Vision Engineer", github: "", demo: "", image: null },
];

const achievements = [
  { title: "Best in Math", year: "JHS", description: "Academic Year 2019–2020" }
  // { title: "Dean's Lister", year: "2023", description: "Academic Year 2022–2023, 1st Semester" },
];

const competitions = [
  // { name: "Programming Contest", year: "2023", role: "Participant", award: "Finalist", organizer: "FAITH Colleges" },
];

const leadership = [
  { position: "Class Treasurer", organization: "United Club", year: "2021-2022", description: "Managing club finances." }
  // { position: "Class Representative", organization: "CPE 3A", year: "2023–2024", description: "Coordinated class activities." },
];

const certificates = [
  { title: "Backend Development Foundations: From Concepts to Practice", organizer: "Institute of Computer Engineers of the Philippines Student Edition", date: "March 2026", image: c1 },
  { title: "Building Sustainable Future: Empowering Communities through HIV Awareness and Education", organizer: "Shaniz Kae C. Cozme", date: "December 2024", image: c2 },
  { title: "Synergizing Technologies: Engineer Oscar's Perspectives on  Meachatronics, IoT, and Genai", organizer: "Jericho Padura", date: "May 2024", image: c3 },
  { title: "Engineering the Future: Career Stories and Industry Insights", organizer: "David Martin T. Butabara & Jafeth C. Estocado", date: "March 29, 2025", image: c4 },
  { title: "Workshop Part 2 on IoT and PLC: Applications in  industry 4.0", organizer: "FAITH Computer Engineering Society", date: "September 13, 2025", image: c5 }
  // { title: "Introduction to IoT", organizer: "Cisco Networking Academy", date: "March 2023", image: null },

];

const navLinks = [
  { label: "Home", to: "home" },
  { label: "About", to: "about" },
  { label: "Education", to: "education" },
  { label: "Skills", to: "skills" },
  { label: "Projects", to: "projects" },
  { label: "Achievements", to: "achievements" },
  { label: "Competitions", to: "competitions" },
  { label: "Leadership", to: "leadership" },
  { label: "Certificates", to: "certificates" },
  { label: "Resume", to: "resume" },
  { label: "Contact", to: "contact" },
];

// ============================================================
// COMPONENTS
// ============================================================

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return (
    <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
      <div className="navbar__inner">
        <div className="navbar__brand">
          <span className="navbar__initials">DJ</span>
          <span className="navbar__name">{personalInfo.firstName}</span>
        </div>
        <button className={`navbar__toggle ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
          <span /><span /><span />
        </button>
        <ul className={`navbar__links ${menuOpen ? 'navbar__links--open' : ''}`}>
          {navLinks.map((link) => (
            <li key={link.to}>
              <Link to={link.to} smooth={true} duration={600} offset={-70} spy={true} activeClass="active" onClick={() => setMenuOpen(false)}>{link.label}</Link>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}

function Home() {
  return (
    <section id="home" className="home">
      <div className="home__grid" />
      <div className="home__glow home__glow--1" />
      <div className="home__glow home__glow--2" />
      <div className="container home__container">
        <div className="home__content">
          <p className="home__pre-label"><span className="home__dot" />Hello, World! I am</p>
          <h1 className="home__name">
            {personalInfo.name.split(' ').map((word, i) => (
              <span key={i} className="home__name-word" style={{ animationDelay: `${i * 0.1}s` }}>{word} </span>
            ))}
          </h1>
          <div className="home__title-row">
            <span className="home__title-bar" />
            <p className="home__title">{personalInfo.title}</p>
            <span className="home__title-bar" />
          </div>
          <p className="home__subtitle">{personalInfo.subtitle}</p>
          <blockquote className="home__tagline">"{personalInfo.tagline}"</blockquote>
          <div className="home__meta">
            <span className="home__meta-item"><span className="home__meta-dot" />{personalInfo.school}</span>
            <span className="home__meta-sep">·</span>
            <span className="home__meta-item"><span className="home__meta-dot" />{personalInfo.yearLevel} — {personalInfo.course}</span>
          </div>
          <div className="home__actions">
            <Link to="projects" smooth={true} duration={600} offset={-70}><button className="btn btn-primary">View My Work</button></Link>
            <Link to="contact" smooth={true} duration={600} offset={-70}><button className="btn btn-outline">Get in Touch</button></Link>
          </div>
          <div className="home__socials">
            <a href={personalInfo.github} target="_blank" rel="noreferrer"><FiGithub /></a>
            <a href={personalInfo.linkedin} target="_blank" rel="noreferrer"><FiLinkedin /></a>
            <a href={`mailto:${personalInfo.email}`}><FiMail /></a>
          </div>
        </div>
        <div className="home__photo-wrapper">
          <div className="home__photo-frame">
            {personalInfo.photo ? (
              <img src={personalInfo.photo} alt={personalInfo.name} className="home__photo" />
            ) : (
              <div className="home__photo-placeholder">
                <span className="home__photo-initials">DJ</span>
                <p>Add photo in<br /><code>App.js</code></p>
              </div>
            )}
          </div>
          <div className="home__photo-deco" />
          <div className="home__photo-deco-2" />
        </div>
      </div>
      <Link to="about" smooth={true} duration={600} offset={-70} className="home__scroll-hint">
        <span>Scroll Down</span>
        <FiArrowDown className="home__scroll-icon" />
      </Link>
    </section>
  );
}

function About() {
  return (
    <section id="about" className="section about">
      <div className="container">
        <div className="section-header">
          <span className="section-label">01. About</span>
          <h2 className="section-title">Who I <span>Am</span></h2>
          <div className="section-divider" />
        </div>
        <div className="about__grid">
          <div className="about__text">
            <p className="about__intro">{aboutMe.intro}</p>
            <p className="about__para">{aboutMe.whyCPE}</p>
            <p className="about__para">{aboutMe.interests}</p>
            <p className="about__para">{aboutMe.goals}</p>
            <div className="about__details">
              <div className="about__detail-item"><span className="about__detail-label">School</span><span className="about__detail-value">{personalInfo.school}</span></div>
              <div className="about__detail-item"><span className="about__detail-label">Course</span><span className="about__detail-value">{personalInfo.course}</span></div>
              <div className="about__detail-item"><span className="about__detail-label">Year Level</span><span className="about__detail-value">{personalInfo.yearLevel}</span></div>
              <div className="about__detail-item"><span className="about__detail-label">Focus Area</span><span className="about__detail-value">Hardware-Software Integration</span></div>
            </div>
          </div>
          <div className="about__strengths-col">
            <div className="card">
              <h3 className="about__strengths-title">Core Strengths</h3>
              <ul className="about__strengths-list">
                {aboutMe.strengths.map((s) => <li key={s} className="about__strength-item"><span className="about__strength-icon">◆</span>{s}</li>)}
              </ul>
            </div>
            <div className="card about__quote-card">
              <p className="about__quote">"After joining engineering. I really appreciate my brain which is divided into two parts right-left. In right, nothing is left. In left, nothing is right."</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Education() {
  return (
    <section id="education" className="section" style={{ position: 'relative' }}>
      <div className="edu__bg-deco" />
      <div className="container">
        <div className="section-header">
          <span className="section-label">02. Education</span>
          <h2 className="section-title">Academic <span>Background</span></h2>
          <div className="section-divider" />
        </div>
        <div className="edu__timeline">
          {education.map((edu, i) => (
            <div key={i} className={`edu__item ${edu.status === 'Current' ? 'edu__item--current' : ''}`}>
              <div className="edu__line-col">
                <div className="edu__dot" />
                {i < education.length - 1 && <div className="edu__connector" />}
              </div>
              <div className="card edu__card">
                {edu.status === 'Current' && <span className="edu__badge">Current</span>}
                <div className="edu__header">
                  <div><h3 className="edu__school">{edu.school}</h3><p className="edu__degree">{edu.degree}</p></div>
                  <div className="edu__period-badge">{edu.period}</div>
                </div>
                {edu.yearLevel && <p className="edu__year">Year Level: <strong>{edu.yearLevel}</strong></p>}
                <p className="edu__details-text">{edu.details}</p>
                {edu.relevantCourses.length > 0 && (
                  <div>
                    <p className="edu__courses-label">Relevant Coursework</p>
                    <div className="edu__courses-list">{edu.relevantCourses.map((c) => <span key={c} className="tag">{c}</span>)}</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SkillBar({ name, level }) {
  return (
    <div className="skill-bar">
      <div className="skill-bar__header"><span className="skill-bar__name">{name}</span><span className="skill-bar__level">{level}%</span></div>
      <div className="skill-bar__track"><div className="skill-bar__fill" style={{ width: `${level}%` }} /></div>
    </div>
  );
}

function Skills() {
  return (
    <section id="skills" className="section">
      <div className="container">
        <div className="section-header">
          <span className="section-label">03. Skills</span>
          <h2 className="section-title">Technical <span>Expertise</span></h2>
          <div className="section-divider" />
        </div>
        <div className="skills__grid">
          <div className="card">
            <h3 className="skills__col-title"><span className="skills__col-icon">{'</>'}</span> Programming Languages</h3>
            {skills.programmingLanguages.map((s) => <SkillBar key={s.name} {...s} />)}
          </div>
          <div className="card">
            <h3 className="skills__col-title"><span className="skills__col-icon">⚡</span> Web Development</h3>
            {skills.webDevelopment.map((s) => <SkillBar key={s.name} {...s} />)}
            <h3 className="skills__col-title" style={{ marginTop: 28 }}><span className="skills__col-icon">🔧</span> Hardware & Tools</h3>
            {skills.hardwareTools.map((s) => <SkillBar key={s.name} {...s} />)}
          </div>
        </div>
        <div className="skills__soft">
          <h3 className="skills__soft-title">Soft Skills</h3>
          <div className="skills__soft-list">{skills.softSkills.map((s) => <span key={s} className="skills__soft-tag">{s}</span>)}</div>
        </div>
      </div>
    </section>
  );
}

const PROJECT_CATEGORIES = ['All', ...new Set(projects.map(p => p.category))];

function Projects() {
  const [filter, setFilter] = useState('All');
  const filtered = filter === 'All' ? projects : projects.filter((p) => p.category === filter);
  return (
    <section id="projects" className="section">
      <div className="container">
        <div className="section-header">
          <span className="section-label">04. Projects</span>
          <h2 className="section-title">My <span>Work</span></h2>
          <div className="section-divider" />
        </div>
        <div className="projects__filters">
          {PROJECT_CATEGORIES.map((cat) => (
            <button key={cat} className={`projects__filter-btn ${filter === cat ? 'active' : ''}`} onClick={() => setFilter(cat)}>{cat}</button>
          ))}
        </div>
        <div className="projects__grid">
          {filtered.map((proj) => (
            <div key={proj.id} className="card project-card">
              <div className="project-card__img">
                {proj.image ? <img src={proj.image} alt={proj.title} /> : <div className="project-card__img-placeholder"><span>{proj.category.split('/')[0]}</span></div>}
              </div>
              <div className="project-card__body">
                <span className="project-card__category">{proj.category}</span>
                <h3 className="project-card__title">{proj.title}</h3>
                <p className="project-card__desc">{proj.description}</p>
                <p className="project-card__role"><span className="project-card__role-label">Role: </span>{proj.role}</p>
                <div className="project-card__tools">{proj.tools.map((t) => <span key={t} className="tag">{t}</span>)}</div>
                <div className="project-card__links">
                  {proj.github && <a href={proj.github} target="_blank" rel="noreferrer" className="project-card__link"><FiGithub /> GitHub</a>}
                  {proj.demo && <a href={proj.demo} target="_blank" rel="noreferrer" className="project-card__link"><FiExternalLink /> Demo</a>}
                  {!proj.github && !proj.demo && <span className="project-card__no-link">Links coming soon</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Achievements() {
  return (
    <section id="achievements" className="section">
      <div className="container">
        <div className="section-header">
          <span className="section-label">05. Achievements</span>
          <h2 className="section-title">Academic <span>Achievements</span></h2>
          <div className="section-divider" />
        </div>
        {achievements.length === 0 ? (
          <div className="empty-state"><span style={{ fontSize: '2rem' }}>🏅</span><p>Add your achievements in the <code style={{ color: 'var(--gold)' }}>achievements</code> array in App.js</p></div>
        ) : (
          <div className="achievements__grid">
            {achievements.map((ach, i) => (
              <div key={i} className="card achievement-card">
                <div className="achievement-card__icon">🏆</div>
                <div><h3 className="achievement-card__title">{ach.title}</h3><span className="achievement-card__year">{ach.year}</span><p className="achievement-card__desc">{ach.description}</p></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Competitions() {
  return (
    <section id="competitions" className="section">
      <div className="container">
        <div className="section-header">
          <span className="section-label">06. Competitions</span>
          <h2 className="section-title">Competitions <span>Joined</span></h2>
          <div className="section-divider" />
        </div>
        {competitions.length === 0 ? (
          <div className="empty-state"><span style={{ fontSize: '2rem' }}>🥇</span><p>Add your competitions in the <code style={{ color: 'var(--gold)' }}>competitions</code> array in App.js</p></div>
        ) : (
          <div className="competitions__list">
            {competitions.map((comp, i) => (
              <div key={i} className="card competition-card">
                <div className="competition-card__left"><span className="competition-card__number">{String(i + 1).padStart(2, '0')}</span></div>
                <div className="competition-card__body">
                  <h3 className="competition-card__name">{comp.name}</h3>
                  <div className="competition-card__meta">
                    <span className="competition-card__meta-item">📅 {comp.year}</span>
                    <span className="competition-card__meta-item">👤 {comp.role}</span>
                    <span className="competition-card__meta-item">🏢 {comp.organizer}</span>
                  </div>
                  {comp.award && <span className="competition-card__award">🏆 {comp.award}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Leadership() {
  return (
    <section id="leadership" className="section">
      <div className="container">
        <div className="section-header">
          <span className="section-label">07. Leadership</span>
          <h2 className="section-title">Leadership & <span>Organizations</span></h2>
          <div className="section-divider" />
        </div>
        {leadership.length === 0 ? (
          <div className="empty-state"><span style={{ fontSize: '2rem' }}>👥</span><p>Add your leadership roles in the <code style={{ color: 'var(--gold)' }}>leadership</code> array in App.js</p></div>
        ) : (
          <div className="leadership__grid">
            {leadership.map((item, i) => (
              <div key={i} className="card">
                <div className="leadership-card__top"><div className="leadership-card__icon">◈</div><span className="leadership-card__year">{item.year}</span></div>
                <h3 className="leadership-card__position">{item.position}</h3>
                <p className="leadership-card__org">{item.organization}</p>
                <p className="leadership-card__desc">{item.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Certificates() {
  const [selected, setSelected] = useState(null);
  return (
    <section id="certificates" className="section">
      <div className="container">
        <div className="section-header">
          <span className="section-label">08. Certificates</span>
          <h2 className="section-title">Certificates & <span>Seminars</span></h2>
          <div className="section-divider" />
        </div>
        {certificates.length === 0 ? (
          <div className="empty-state"><span style={{ fontSize: '2rem' }}>📜</span><p>Add your certificates in the <code style={{ color: 'var(--gold)' }}>certificates</code> array in App.js</p></div>
        ) : (
          <div className="certificates__grid">
            {certificates.map((cert, i) => (
              <div key={i} className="card cert-card" onClick={() => cert.image && setSelected(cert)}>
                <div className="cert-card__img-area">
                  {cert.image ? <img src={cert.image} alt={cert.title} className="cert-card__img" /> : <div className="cert-card__img-placeholder"><span>📜</span></div>}
                </div>
                <div className="cert-card__body">
                  <h3 className="cert-card__title">{cert.title}</h3>
                  <p className="cert-card__organizer">{cert.organizer}</p>
                  <span className="cert-card__date">{cert.date}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {selected && (
          <div className="cert-modal" onClick={() => setSelected(null)}>
            <div className="cert-modal__inner" onClick={(e) => e.stopPropagation()}>
              <button className="cert-modal__close" onClick={() => setSelected(null)}>✕</button>
              <img src={selected.image} alt={selected.title} className="cert-modal__img" />
              <h3 className="cert-modal__title">{selected.title}</h3>
              <p className="cert-modal__sub">{selected.organizer} — {selected.date}</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function Resume() {
  return (
    <section id="resume" className="section">
      <div className="container">
        <div className="section-header">
          <span className="section-label">09. Resume</span>
          <h2 className="section-title">My <span>Resume</span></h2>
          <div className="section-divider" />
        </div>
        <div className="resume__card card">
          <div className="resume__deco" />
          <div className="resume__content">
            <span className="resume__icon">📄</span>
            <h3 className="resume__name">{personalInfo.name}</h3>
            <p className="resume__title-text">{personalInfo.title}</p>
            <p className="resume__school-text">{personalInfo.course} — {personalInfo.school}</p>
            <p className="resume__note">Download or view the full resume to learn more about my academic background, skills, projects, and achievements.</p>
            <div className="resume__actions">
              <a href="/resume.pdf" download className="btn btn-primary"><FiDownload /> Download PDF Resume</a>
              <a href="/resume.pdf" target="_blank" rel="noreferrer" className="btn btn-outline"><FiEye /> View Resume</a>
            </div>
            <p className="resume__hint">📌 Place your <code>resume.pdf</code> inside the <code>public/</code> folder.</p>
          </div>
        </div>
        <div className="resume__preview-grid">
          {[
            { icon: '🎓', label: 'Education', desc: 'FAITH Colleges — BS Computer Engineering, 3rd Year' },
            { icon: '⚙️', label: 'Skills', desc: 'C++, Python, Java, ReactJS, HTML/CSS, Arduino, Verilog HDL' },
            { icon: '💼', label: 'Projects', desc: 'IoT systems, embedded projects, web apps, mobile apps' },
            { icon: '🏅', label: 'Achievements', desc: 'Academic honors and recognitions' },
          ].map((item) => (
            <div key={item.label} className="card resume__preview-card">
              <span className="resume__preview-icon">{item.icon}</span>
              <h4 className="resume__preview-label">{item.label}</h4>
              <p className="resume__preview-desc">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = (e) => {
    e.preventDefault();
    const mailto = `mailto:${personalInfo.email}?subject=${encodeURIComponent(form.subject)}&body=${encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`)}`;
    window.open(mailto, '_blank');
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
  };
  const contactItems = [
    { icon: <FiMail />, label: 'Email', value: personalInfo.email, href: `mailto:${personalInfo.email}` },
    { icon: <FiGithub />, label: 'GitHub', value: 'github.com/dwaynelagamayo', href: personalInfo.github },
    { icon: <FiLinkedin />, label: 'LinkedIn', value: 'linkedin.com/in/dwaynelagamayo', href: personalInfo.linkedin },
    { icon: <FiMapPin />, label: 'Location', value: personalInfo.location, href: null },
  ];
  return (
    <section id="contact" className="section">
      <div className="container">
        <div className="section-header">
          <span className="section-label">10. Contact</span>
          <h2 className="section-title">Get In <span>Touch</span></h2>
          <div className="section-divider" />
          <p className="contact__subtitle">Have a project in mind, want to collaborate, or just want to say hi?</p>
        </div>
        <div className="contact__grid">
          <div className="contact__info">
            <h3 className="contact__info-title">Let's Connect</h3>
            <p className="contact__info-text">I'm always open to new opportunities, whether it's an internship, project collaboration, or just a conversation about technology.</p>
            <div className="contact__items">
              {contactItems.map((item) => (
                <div key={item.label} className="contact__item">
                  <div className="contact__item-icon">{item.icon}</div>
                  <div>
                    <p className="contact__item-label">{item.label}</p>
                    {item.href ? <a href={item.href} target="_blank" rel="noreferrer" className="contact__item-value contact__item-value--link">{item.value}</a> : <p className="contact__item-value">{item.value}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="card contact__form-card">
            {submitted ? (
              <div className="contact__success"><span>✅</span><p>Opening your email client...</p></div>
            ) : (
              <form className="contact__form" onSubmit={handleSubmit}>
                <div className="contact__form-row">
                  <div className="contact__field"><label className="contact__label">Your Name</label><input name="name" value={form.name} onChange={handleChange} placeholder="Juan dela Cruz" className="contact__input" required /></div>
                  <div className="contact__field"><label className="contact__label">Your Email</label><input name="email" type="email" value={form.email} onChange={handleChange} placeholder="juan@email.com" className="contact__input" required /></div>
                </div>
                <div className="contact__field"><label className="contact__label">Subject</label><input name="subject" value={form.subject} onChange={handleChange} placeholder="Project Collaboration" className="contact__input" required /></div>
                <div className="contact__field"><label className="contact__label">Message</label><textarea name="message" value={form.message} onChange={handleChange} placeholder="Tell me about your project..." className="contact__input contact__textarea" rows={5} required /></div>
                <button type="submit" className="btn btn-primary contact__submit"><FiSend /> Send Message</button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container footer__inner">
        <div className="footer__brand">
          <span className="footer__initials">DJ</span>
          <div><p className="footer__fname">{personalInfo.name}</p><p className="footer__ftitle">{personalInfo.title}</p></div>
        </div>
        <p className="footer__copy">© {new Date().getFullYear()} {personalInfo.name}. Built with ReactJS.</p>
        <div className="footer__socials">
          <a href={personalInfo.github} target="_blank" rel="noreferrer"><FiGithub /></a>
          <a href={personalInfo.linkedin} target="_blank" rel="noreferrer"><FiLinkedin /></a>
          <a href={`mailto:${personalInfo.email}`}><FiMail /></a>
        </div>
      </div>
    </footer>
  );
}

function App() {
  return (
    <>
      <Navbar />
      <main>
        <Home />
        <About />
        <Education />
        <Skills />
        <Projects />
        <Achievements />
        <Competitions />
        <Leadership />
        <Certificates />
        <Resume />
        <Contact />
      </main>
      <Footer />
    </>
  );
}

export default App;